/**
 * PATH: packages/email-hub/src/feature/email-layout/EmailSidebarTools.tsx
 * FILE_ID: EMAIL-LAYOUT-SIDEBAR-TOOLS-001
 * PURPOSE: Render expanded and collapsed Email utility navigation.
 */

import { Activity, Bot, CheckCircle2, Settings, Tag } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '../../_internal/lib/utils'
import { emailPath } from '../lib/basePath'
import type { EmailLayoutController } from './useEmailLayoutController'

function ExpandedTools({ layout }: { layout: EmailLayoutController }) {
  return (
    <>
      <div className="px-3 mt-2 mb-1">
        <p className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
          Tools
        </p>
      </div>
      <div className="px-2 space-y-0.5 mb-2">
        <button
          type="button"
          onClick={() => layout.openSettings('email-sync')}
          className="w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          title="Sync status, history backfill progress and live mailbox diagnostics"
        >
          <span className="flex items-center gap-2.5">
            <Activity className="w-4 h-4" /> Sync &amp; Status
          </span>
          {!layout.backfill.state.complete && layout.backfill.progressPct > 0 && (
            <span className="text-[10px] font-mono text-muted-foreground">
              {layout.backfill.progressPct}%
            </span>
          )}
          {layout.backfill.state.complete && (
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
          )}
        </button>
        <button
          type="button"
          onClick={() => layout.openSettings('email')}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <Settings className="w-4 h-4" /> Email Settings
        </button>
        <button
          type="button"
          onClick={() => layout.openSettings('email-ai')}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          title="AI auto-categorisation policy - kill-switch, mode and auto-accept confidence"
        >
          <Bot className="w-4 h-4" /> AI Triage
        </button>
        <Link
          to={emailPath('/tags-rules')}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <Tag className="w-4 h-4" /> Tags &amp; Rules
        </Link>
      </div>
    </>
  )
}

function CollapsedTools({ layout }: { layout: EmailLayoutController }) {
  return (
    <div className="px-1.5 space-y-0.5 mb-2">
      <button
        type="button"
        onClick={() => layout.openSettings('email-sync')}
        className={cn(
          'relative flex items-center justify-center h-9 w-9 mx-auto rounded-lg transition-colors',
          'text-muted-foreground hover:bg-accent hover:text-foreground',
        )}
        title={
          layout.backfill.state.complete
            ? 'Sync & Status - history caught up'
            : `Sync & Status - ${layout.backfill.progressPct}% backfilled`
        }
      >
        <Activity className="w-4 h-4" />
        {!layout.backfill.state.complete && layout.backfill.progressPct > 0 && (
          <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-primary rounded-full" />
        )}
      </button>
      <button
        type="button"
        onClick={() => layout.openSettings('email')}
        className="flex items-center justify-center h-9 w-9 mx-auto rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        title="Email Settings"
      >
        <Settings className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => layout.openSettings('email-ai')}
        className="flex items-center justify-center h-9 w-9 mx-auto rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        title="AI Triage - auto-categorisation policy"
      >
        <Bot className="w-4 h-4" />
      </button>
    </div>
  )
}

export function EmailSidebarTools({
  layout,
}: {
  layout: EmailLayoutController
}) {
  return layout.collapsed ? (
    <CollapsedTools layout={layout} />
  ) : (
    <ExpandedTools layout={layout} />
  )
}
